import api from './api';
import { cashfreeConfig, isCashfreeLive } from '../config/artTasteConfig';

export function normalizeOrder(order) {
  if (!order) {
    return order;
  }

  return {
    ...order,
    orderId: order.orderId || order.order_id || order.id,
    createdAt: order.createdAt || order.created_at,
    updatedAt: order.updatedAt || order.updated_at,
    totalAmount: order.totalAmount ?? order.total_amount ?? 0,
    printOptions: order.printOptions || order.print_options || {},
    paymentMode: order.paymentMode || order.payment_mode || 'demo',
    paymentStatus: order.paymentStatus || order.payment_status || 'pending',
    paymentSessionId: order.paymentSessionId || order.payment_session_id || null,
  };
}

function getCashfreeFactory() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (typeof window.Cashfree === 'function') {
    return window.Cashfree;
  }

  if (typeof window.load === 'function') {
    return window.load;
  }

  return null;
}

function extractErrorText(error) {
  const detail = error?.response?.data?.detail;
  const code = error?.response?.data?.code;
  const message = error?.response?.data?.message;
  return [detail, code, message, error?.message].filter(Boolean).join(' | ');
}

export function getFriendlyErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  const detail = error?.response?.data?.detail;
  const code = error?.response?.data?.code;
  const message = error?.response?.data?.message;
  const rawText = extractErrorText(error).toLowerCase();

  if (code === 'payment_session_id_invalid' || rawText.includes('payment_session_id')) {
    return 'We could not start the payment window. Please try placing the order again.';
  }

  if (code === 'order_not_found' || rawText.includes('order reference id does not exist') || rawText.includes('order_not_found')) {
    return 'We could not find your payment session. Please try placing the order again.';
  }

  if (rawText.includes('failed to create payment session')) {
    return 'We could not start your payment right now. Please try again in a moment.';
  }

  if (rawText.includes('payment session has not been created')) {
    return 'Your order was saved, but payment has not started yet. Please try again.';
  }

  if (rawText.includes('payment could not be completed')) {
    return 'Your payment could not be completed. Please try again.';
  }

  if (rawText.includes('payment is still pending') || rawText.includes('payment pending')) {
    return 'Your payment is still pending. If money was deducted, please wait a moment and check again.';
  }

  if (rawText.includes('network error') || !navigator.onLine) {
    return 'We could not reach the server. Please check your internet connection and try again.';
  }

  if (error?.response?.status === 401) {
    if (rawText.includes('invalid credentials')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (rawText.includes('login expired')) {
      return 'Your session expired. Please sign in again.';
    }
    return 'Please sign in again and retry.';
  }

  if (error?.response?.status === 403) {
    if (rawText.includes('admin access required')) {
      return 'This account does not have admin access.';
    }
    return 'You do not have access to do that action.';
  }

  if (error?.response?.status === 404) {
    return 'We could not find what you were looking for.';
  }

  if (error?.response?.status >= 500) {
    return 'Our server is having trouble right now. Please try again in a moment.';
  }

  if (rawText.includes('email already registered')) {
    return 'An account with this email already exists. Please sign in instead.';
  }

  if (rawText.includes('password must be at least 6 characters')) {
    return 'Your password must be at least 6 characters long.';
  }

  if (rawText.includes('admin access required')) {
    return 'This account does not have admin access.';
  }

  if (rawText.includes('invalid credentials')) {
    return 'Incorrect email or password. Please try again.';
  }

  if (typeof detail === 'string' && detail.trim() && !detail.toLowerCase().includes('traceback')) {
    return detail;
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallback;
}

async function openCashfreeCheckout(paymentSessionId, mode = cashfreeConfig.mode) {
  const factory = getCashfreeFactory();
  if (!factory) {
    throw new Error('Cashfree SDK is not loaded on this page.');
  }

  const maybeClient = factory({ mode });
  const client =
    maybeClient && typeof maybeClient.then === 'function'
      ? await maybeClient
      : maybeClient;

  if (!client || typeof client.checkout !== 'function') {
    throw new Error('Cashfree checkout client is unavailable.');
  }

  const result = await client.checkout({
    paymentSessionId,
    redirectTarget: '_modal',
  });

  if (result?.error) {
    throw new Error(getFriendlyErrorMessage(result.error, 'Payment could not be completed.'));
  }

  return result;
}

async function uploadOrderFiles(orderId, files, onProgress) {
  const uploadedFiles = [];

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    const { data } = await api.post('/r2/presigned-url', {
      filename: `${orderId}_${Date.now()}_${file.name}`,
      content_type: file.type || 'application/octet-stream',
    });

    const uploadUrl = data.upload_url;
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });

    const progress = Math.round(((index + 1) / files.length) * 100);
    if (onProgress) {
      onProgress({
        visible: true,
        currentFile: file.name,
        fileIndex: index + 1,
        totalFiles: files.length,
        overallProgress: progress,
      });
    }

    uploadedFiles.push({
      name: file.name,
      size: file.size,
      type: file.type,
      url: data.public_url,
      path: data.file_key,
    });
  }

  return uploadedFiles;
}

async function createCashfreeSession(orderId, totalAmount) {
  const origin_url = window.location.origin;
  const response = await api.post('/payments/checkout', {
    order_id: orderId,
    origin_url,
  });
  return response.data;
}

export async function verifyOrderPayment(orderId) {
  try {
    const response = await api.get(`/payments/verify/${orderId}`);
    return response.data;
  } catch (error) {
    throw new Error(getFriendlyErrorMessage(error, 'We could not verify your payment status.'));
  }
}

export async function submitOrder({ orderInfo, files, onProgress, uploadOnly = false, paymentOnly = false }) {
  const orderId = orderInfo.orderId || `AT-${Date.now()}`;

  let uploadedFiles = files;
  if (!paymentOnly) {
    uploadedFiles = await uploadOrderFiles(orderId, files, onProgress);
  }

  const orderPayload = {
    order_id: orderId,
    customer: orderInfo.customer,
    print_options: orderInfo.printOptions,
    files: uploadedFiles,
    total_amount: orderInfo.totalAmount,
    notes: orderInfo.notes || '',
    payment_mode: isCashfreeLive ? 'live' : 'demo',
  };

  try {
    await api.post('/orders', orderPayload);
  } catch (error) {
    throw new Error(getFriendlyErrorMessage(error, 'We could not save your order. Please try again.'));
  }

  if (uploadOnly) {
    // Return early for upload-only mode (order created but no payment)
    return {
      orderId,
      uploadedFiles,
    };
  }

  if (isCashfreeLive) {
    let sessionData;
    try {
      sessionData = await createCashfreeSession(orderId, orderInfo.totalAmount);
    } catch (error) {
      throw new Error(getFriendlyErrorMessage(error, 'We could not start your payment right now.'));
    }
    if (sessionData.payment_status === 'paid') {
      return {
        orderId,
        sessionId: sessionData.payment_session_id || sessionData.session_id || null,
        paymentMode: 'live',
      };
    }

    const sessionId = sessionData.session_id || sessionData.payment_session_id;
    if (!sessionId) {
      throw new Error('Cashfree session could not be created for this order.');
    }

    const checkoutMode = sessionData.cashfree_mode || cashfreeConfig.mode;
    await openCashfreeCheckout(sessionId, checkoutMode);
    const verification = await verifyOrderPayment(orderId);

    if (verification.payment_status !== 'paid') {
      throw new Error(
        verification.payment_message || 'Payment is still pending. Please complete the Cashfree payment and try again.',
      );
    }

    return {
      orderId,
      sessionId,
      paymentMode: 'live',
    };
  }

  return {
    orderId,
    paymentMode: 'demo',
  };
}

export async function signInUser(email, password) {
  const response = await api.post('/auth/login', { email, password });
  const { token, refresh_token, user } = response.data;
  window.localStorage.setItem('auth_token', token);
  window.localStorage.setItem('refresh_token', refresh_token);
  window.localStorage.setItem('auth_user', JSON.stringify(user));
  return user;
}

export async function signUpUser(name, email, password) {
  const response = await api.post('/auth/register', { name, email, password });
  const { token, refresh_token, user } = response.data;
  window.localStorage.setItem('auth_token', token);
  window.localStorage.setItem('refresh_token', refresh_token);
  window.localStorage.setItem('auth_user', JSON.stringify(user));
  return user;
}

export async function updateUserPassword(currentPassword, newPassword) {
  const response = await api.put('/users/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
}

export async function signOutUser() {
  window.localStorage.removeItem('auth_token');
  window.localStorage.removeItem('refresh_token');
  window.localStorage.removeItem('auth_user');
}

export function getCurrentUser() {
  const user = window.localStorage.getItem('auth_user');
  return user ? JSON.parse(user) : null;
}

export function isAuthenticated() {
  return !!getCurrentUser();
}

export function isAdminUser(user = getCurrentUser()) {
  return user?.role === 'admin';
}

// Admin functions (for backward compatibility)
export async function signInAdmin(email, password) {
  const user = await signInUser(email, password);

  if (user?.role !== 'admin') {
    await signOutUser();
    throw new Error('Admin access required');
  }

  return user;
}

export async function signOutAdmin() {
  return signOutUser();
}

export function subscribeToAdminSession(callback) {
  const user = window.localStorage.getItem('auth_user');
  callback(user ? JSON.parse(user) : null);
  return () => { };
}

export async function listOrders() {
  const response = await api.get('/orders');
  return response.data.map(normalizeOrder);
}

export async function updateOrderStatus(orderId, status) {
  await api.put(`/orders/${orderId}/status`, { status });
}
